import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AddBox as AddColumnIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Link as LinkIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenInNewIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  SwapHoriz as ReassignIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { crm, fmtDate, fmtDateTime, fromNow, inr } from '../../../api/crm';
import { useSales } from '../../../context/SalesContext';
import LeadFilters, { cleanFilters, countFilters } from './LeadFilters';
import LeadDetails from './LeadDetails';
import { Initials, Tag, planTone } from './ui';

const PAGE_SIZES = [25, 50, 100];

const istDate = (offsetDays = 0) => {
  // YYYY-MM-DD of "today" in IST, shifted by offsetDays.
  const d = new Date(Date.now() + 5.5 * 3600e3 + offsetDays * 86400e3);
  return d.toISOString().slice(0, 10);
};

// Client-side quick date chips (they just set the install / registration date filters).
const quickDates = (segment) => {
  const from = segment === 'webinar' ? 'webinar_from' : 'installed_from';
  const to = segment === 'webinar' ? 'webinar_to' : 'installed_to';
  const word = segment === 'webinar' ? 'Registered' : 'Installed';
  return [
    { key: 'today', label: `${word} today`, set: { [from]: istDate(0), [to]: istDate(0) } },
    { key: 'yesterday', label: `${word} yesterday`, set: { [from]: istDate(-1), [to]: istDate(-1) } },
    { key: 'week', label: 'Last 7 days', set: { [from]: istDate(-7), [to]: istDate(0) } },
  ].map((q) => ({ ...q, from, to }));
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const daysUntil = (v) => (v ? Math.ceil((new Date(v).getTime() - Date.now()) / 86400e3) : null);
const hidden = (name) => ['owner'].includes(String(name).toLowerCase());

// signalsOf lists what makes this lead worth a call right now, most urgent first.
const signalsOf = (lead) => {
  const out = [];
  if (lead.last_callback_at && Date.now() - new Date(lead.last_callback_at).getTime() < 7 * 86400e3) {
    out.push({ key: 'cb', tone: 'warning', label: `Callback ${fromNow(lead.last_callback_at)}` });
  }
  if (lead.open_failed) {
    out.push({ key: 'fp', tone: 'error', label: `Failed ${inr(lead.last_failed_amount)} · ${fromNow(lead.last_failed_at)}` });
  }
  if (lead.next_followup_at) {
    const overdue = new Date(lead.next_followup_at) < new Date();
    out.push({ key: 'fu', tone: overdue ? 'error' : 'info', label: `Follow-up ${overdue ? 'overdue' : fromNow(lead.next_followup_at)}` });
  }
  if (lead.link_active) out.push({ key: 'ln', tone: 'purple', label: 'Link sent' });
  const planExpiry = [lead.algo_plan_expiry, lead.research_expiry].filter(Boolean).sort().pop();
  const d = daysUntil(planExpiry);
  if (lead.plan_active && d !== null && d <= 15) out.push({ key: 'ex', tone: 'warning', label: d <= 0 ? 'Expires today' : `Expires in ${d}d` });
  if (lead.algo_plan_active && !lead.live_algos) out.push({ key: 'nl', tone: 'neutral', label: 'Paid, not live' });
  if (lead.coupon_applied_at && (!lead.last_paid_at || new Date(lead.last_paid_at) < new Date(lead.coupon_applied_at))
    && Date.now() - new Date(lead.coupon_applied_at).getTime() < 3 * 86400e3) {
    out.push({ key: 'cp', tone: 'info', label: 'Coupon applied' });
  }
  return out;
};

export const webinarStage = (lead) => {
  if (lead.paid_count > 0) return { label: 'Bought a plan', tone: 'success' };
  if (lead.is_app_user) return { label: 'Joined app', tone: 'info' };
  if (lead.webinar_paid_amount > 0) return { label: 'Paid webinar', tone: 'primary' };
  return { label: 'Registered', tone: 'neutral' };
};

const Signals = ({ lead, max }) => {
  const s = signalsOf(lead);
  const shown = max ? s.slice(0, max) : s;
  if (s.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {shown.map((x) => <Tag key={x.key} tone={x.tone} dot>{x.label}</Tag>)}
      {s.length > shown.length && <Tag tone="neutral">+{s.length - shown.length}</Tag>}
    </Box>
  );
};

export const PlanText = ({ lead }) => {
  if (!lead.algo_plan && !lead.research_plan) return <Typography variant="body2" color="text.disabled">—</Typography>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, alignItems: 'flex-start' }}>
      {lead.algo_plan && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
          <Tag tone={planTone(lead.algo_plan, lead.algo_plan_active)}>{titleCase(lead.algo_plan)}</Tag>
          <Typography variant="caption" color={lead.algo_plan_active ? 'text.secondary' : 'error.main'} sx={{ whiteSpace: 'nowrap' }}>
            {lead.algo_plan_active ? 'till' : 'expired'} {fmtDate(lead.algo_plan_expiry)}
          </Typography>
        </Box>
      )}
      {lead.research_plan && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Research {lead.research_plan} · {fmtDate(lead.research_expiry)}
        </Typography>
      )}
    </Box>
  );
};

// OwnerTag: who works the lead (a warning when nobody does).
const OwnerTag = ({ owner }) => (owner ? (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
    <Initials name={owner} seed={owner} size={20} />
    <Typography variant="body2">{owner}</Typography>
  </Box>
) : <Tag tone="warning">Unassigned</Tag>);

const pnlSx = (v) => ({ color: v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.secondary' });

const LeadsBoard = ({ segment = 'app' }) => {
  const sales = useSales();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [, setSearchParams] = useSearchParams();
  // The remembered view is per person (several people may share a browser).
  const storeKey = `sales-board-${segment}-${sales.me?.number || ''}`;

  const saved = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storeKey) || '{}') || {};
    } catch (e) {
      return {};
    }
  }, [storeKey]);

  const [filters, setFilters] = useState(saved.filters || {});
  const [preset, setPreset] = useState(saved.preset || '');
  const [sortBy, setSortBy] = useState(saved.sortBy || 'priority');
  const [sortOrder, setSortOrder] = useState(saved.sortOrder || '');
  const [limit, setLimit] = useState(PAGE_SIZES.includes(saved.limit) ? saved.limit : 25);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restricted, setRestricted] = useState('');
  const [meta, setMeta] = useState(null);
  const [customColumns, setCustomColumns] = useState([]);

  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [followupFor, setFollowupFor] = useState(null);
  const [followupForm, setFollowupForm] = useState({ note: '', remind_at: '' });
  const [reassign, setReassign] = useState(null); // { numbers: [] } | { all: true }
  const [reassignTo, setReassignTo] = useState('');
  const [editing, setEditing] = useState(null); // { number, column, value }
  const [columnDialog, setColumnDialog] = useState(null); // { id?, name, type, values }
  const [menu, setMenu] = useState(null); // { anchor, lead }
  const [busy, setBusy] = useState(false);
  // "Returning users": people active in the app again recently (from app analytics).
  // Not remembered between visits, because the list changes daily.
  const [returning, setReturning] = useState(null);
  const [returningLoading, setReturningLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const reqId = useRef(0);

  const toast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    try {
      localStorage.setItem(storeKey, JSON.stringify({ filters, preset, sortBy, sortOrder, limit }));
    } catch (e) {
      // storage full / private mode: the view just is not remembered
    }
  }, [storeKey, filters, preset, sortBy, sortOrder, limit]);

  const requestBody = useCallback(() => ({
    segment,
    search,
    filters: { ...cleanFilters(filters), ...(preset ? { preset } : {}), ...(returning ? { numbers: returning } : {}) },
  }), [segment, search, filters, preset, returning]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError('');
    try {
      const data = await crm('leads/list', { method: 'POST', body: { ...requestBody(), page, limit, sort_by: sortBy, sort_order: sortOrder } });
      if (id !== reqId.current) return; // a newer request is on its way
      setRows(Array.isArray(data?.leads) ? data.leads : []);
      setTotal(data?.pagination?.total || 0);
      setRestricted(data?.restricted?.message || '');
    } catch (e) {
      if (id !== reqId.current) return;
      setRows([]);
      setTotal(0);
      setError(e.message);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [requestBody, page, limit, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    crm('leads/meta').then(setMeta).catch(() => setMeta({}));
    // Every column is shown, whatever its is_active flag: the flag was never used to hide
    // columns (Dialed, Disposition, Priority, ... are stored with it off).
    crm('custom-columns').then((cols) => setCustomColumns(Array.isArray(cols) ? cols : [])).catch(() => {});
  }, []);

  // Anything that changes the result set goes back to page 1 and clears the selection.
  const reset = () => { setPage(1); setSelected([]); };
  const applyFilters = (f) => { setFilters(f); setFiltersOpen(false); reset(); };
  const applyPreset = (key) => { setPreset(preset === key ? '' : key); reset(); };
  const runSearch = () => { setSearch(searchInput.trim()); reset(); };
  const clearAll = () => { setFilters({}); setPreset(''); setSearch(''); setSearchInput(''); setReturning(null); reset(); };

  const toggleReturning = async () => {
    if (returning) { setReturning(null); reset(); return; }
    setReturningLoading(true);
    try {
      const data = await crm('clients/clientsWithUsersPastActive');
      const numbers = (data?.users || []).map((u) => u?.obalgo_user?.number).filter(Boolean);
      if (numbers.length === 0) toast('No returning users right now', 'info');
      else { setReturning(numbers); reset(); }
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setReturningLoading(false);
    }
  };

  const sorts = meta?.sorts || [{ key: 'priority', label: 'Priority (hot first)', default_order: 'ASC' }];
  const currentSort = sorts.find((s) => s.key === sortBy) || sorts[0];
  const effectiveOrder = (sortOrder || currentSort?.default_order || 'DESC').toUpperCase();
  const visibleColumns = customColumns.filter((c) => !hidden(c.name));
  const dates = quickDates(segment);
  const activeQuick = dates.find((q) => filters[q.from] === q.set[q.from] && filters[q.to] === q.set[q.to]);
  const filterCount = countFilters(filters);
  const members = (sales.team || []).filter((m) => m.active);

  // ---- actions
  const openProfile = (lead) => window.open(`/sales/profile/${lead.number}`, '_blank', 'noopener');
  const openPaymentLink = (lead) => {
    const next = new URLSearchParams();
    next.set('tab', 'payment-links');
    next.set('number', lead.number);
    if (lead.name && !/^webinar_/.test(lead.name)) next.set('name', lead.name);
    setSearchParams(next);
  };

  const saveFollowup = async () => {
    if (!followupForm.note.trim() || !followupForm.remind_at) return;
    setBusy(true);
    try {
      await crm('followups', { method: 'POST', body: { client_number: followupFor.number, note: followupForm.note.trim(), remind_at: new Date(followupForm.remind_at).toISOString() } });
      toast('Follow-up set (only you can see it)');
      setFollowupFor(null);
      setFollowupForm({ note: '', remind_at: '' });
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const doReassign = async () => {
    setBusy(true);
    try {
      const body = reassign.all ? { ...requestBody(), all_matching: true, to: reassignTo } : { numbers: reassign.numbers, to: reassignTo };
      const res = await crm('leads/reassign', { method: 'POST', body });
      toast(`${res.reassigned} lead${res.reassigned === 1 ? '' : 's'} assigned to ${reassignTo || 'nobody'}`);
      setReassign(null);
      setReassignTo('');
      setSelected([]);
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Custom column rules (unchanged from before): Dialed Time is stamped, never typed;
  // Dialed can be set once; a paid customer's fields are locked.
  const canEdit = (lead, column) => {
    const name = column.name.toLowerCase();
    if (name === 'dialed time') return false;
    if (name === 'dialed' && lead.custom_fields?.[column.name]) return false;
    return !(lead.paid_count >= 1);
  };

  const saveField = async (lead, column, value) => {
    const updates = [{ field: column.name, value }];
    if (column.name.toLowerCase() === 'dialed') updates.push({ field: 'Dialed Time', value: new Date().toISOString() });
    try {
      for (const u of updates) {
        // eslint-disable-next-line no-await-in-loop
        await crm('clients/number/custom-fields/update', { method: 'POST', body: { number: lead.number, field: u.field, value: u.value } });
      }
      setRows((prev) => prev.map((r) => (r.number === lead.number
        ? { ...r, custom_fields: { ...(r.custom_fields || {}), ...Object.fromEntries(updates.map((u) => [u.field, u.value])) } }
        : r)));
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setEditing(null);
    }
  };

  const saveWebinarNote = async (lead, patch) => {
    const next = { dialed: Boolean(lead.dialed), remarks: lead.remarks || '', ...patch };
    setRows((prev) => prev.map((r) => (r.number === lead.number ? { ...r, ...next } : r)));
    try {
      await crm('webinar-registration-notes', { method: 'POST', body: { user_number: lead.number, dialed: next.dialed, remarks: next.remarks } });
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const saveColumn = async () => {
    const values = columnDialog.type === 'dropdown' ? columnDialog.values.split(',').map((v) => v.trim()).filter(Boolean) : [];
    try {
      if (columnDialog.id) {
        const updated = await crm('custom-columns/update', { method: 'POST', body: { id: columnDialog.id, name: columnDialog.name, type: columnDialog.type, dropdown_values: values, is_active: true } });
        setCustomColumns((cols) => cols.map((c) => (c.id === columnDialog.id ? updated : c)));
      } else {
        const created = await crm('custom-columns', { method: 'POST', body: { name: columnDialog.name.trim(), type: columnDialog.type, dropdown_values: values } });
        setCustomColumns((cols) => [...cols, created]);
      }
      setColumnDialog(null);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const deleteColumn = async () => {
    if (!window.confirm(`Delete the column "${columnDialog.name}" for everyone?`)) return;
    try {
      await crm(`custom-columns/${columnDialog.id}`, { method: 'DELETE' });
      setCustomColumns((cols) => cols.filter((c) => c.id !== columnDialog.id));
      setColumnDialog(null);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const toggleSelect = (number) => setSelected((s) => (s.includes(number) ? s.filter((n) => n !== number) : [...s, number]));
  const allOnPage = rows.length > 0 && rows.every((r) => selected.includes(r.number));

  // ---- small render helpers
  const fieldEditor = (lead, column) => {
    const value = lead.custom_fields?.[column.name] ?? '';
    const isEditing = editing && editing.number === lead.number && editing.column === column.name;
    if (isEditing && column.type === 'dropdown') {
      return (
        <TextField
          select
          size="small"
          autoFocus
          value={editing.value}
          onChange={(e) => saveField(lead, column, e.target.value)}
          onBlur={() => setEditing(null)}
          SelectProps={{ defaultOpen: true }}
          sx={{ minWidth: 110 }}
        >
          <MenuItem value="">—</MenuItem>
          {(column.dropdown_values || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>
      );
    }
    if (isEditing) {
      return (
        <TextField
          size="small"
          autoFocus
          multiline={column.type === 'long_text'}
          value={editing.value}
          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
          onBlur={() => saveField(lead, column, editing.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && column.type !== 'long_text') saveField(lead, column, editing.value);
            if (e.key === 'Escape') setEditing(null);
          }}
          sx={{ minWidth: 140 }}
        />
      );
    }
    const shown = column.name.toLowerCase() === 'dialed time' ? (value ? fmtDateTime(value) : '') : value;
    const editable = canEdit(lead, column);
    return (
      <Box
        onClick={editable ? () => setEditing({ number: lead.number, column: column.name, value }) : undefined}
        sx={{
          minHeight: 28, minWidth: 60, px: 0.5, borderRadius: 1, display: 'flex', alignItems: 'center',
          cursor: editable ? 'pointer' : 'default',
          '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
          color: shown ? 'text.primary' : 'text.disabled', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8125rem'
        }}
      >
        {shown || (editable ? <Box component="span" className="cell-add" sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.12s', color: 'primary.main', fontWeight: 500 }}>+ Add</Box> : '—')}
      </Box>
    );
  };

  // Call and WhatsApp are the two things a sales person does most: they get a tinted
  // button; the rest are quiet icons.
  const soft = (bg, fg) => ({ width: 30, height: 30, bgcolor: bg, color: fg, '&:hover': { bgcolor: bg, filter: 'brightness(0.95)' } });
  const ghost = { width: 30, height: 30, color: '#8A94A6', '&:hover': { color: 'text.primary' } };
  const actions = (lead, dense) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: dense ? 0.5 : 0.5 }}>
      <Tooltip title="Call"><IconButton size="small" component="a" href={`tel:+91${lead.number}`} sx={soft('#EAF0FF', '#2F54D6')}><PhoneIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
      <Tooltip title="WhatsApp"><IconButton size="small" component="a" href={`https://wa.me/91${lead.number}`} target="_blank" rel="noopener noreferrer" sx={soft('#E6F6EC', '#1A9B4B')}><WhatsAppIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
      {sales.can('payment_links') && <Tooltip title="Payment link"><IconButton size="small" onClick={() => openPaymentLink(lead)} sx={ghost}><LinkIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
      <Tooltip title="Set a follow-up"><IconButton size="small" onClick={() => setFollowupFor(lead)} sx={ghost}><ScheduleIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      {lead.is_app_user && <Tooltip title="Open profile"><IconButton size="small" onClick={() => openProfile(lead)} sx={ghost}><OpenInNewIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>}
      {sales.isManager && <Tooltip title="More"><IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, lead })} sx={ghost}><MoreIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
    </Box>
  );

  const nameOf = (lead) => (lead.name && !/^webinar_/.test(lead.name) ? lead.name : '');

  // ---- mobile: one card per lead
  const card = (lead) => {
    const open = Boolean(expanded[lead.number]);
    const stage = webinarStage(lead);
    return (
      <Paper key={lead.number} variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: selected.includes(lead.number) ? 'primary.main' : 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box onClick={sales.isManager ? () => toggleSelect(lead.number) : undefined} sx={{ position: 'relative' }}>
            <Initials name={nameOf(lead)} seed={lead.number} size={38} />
            {sales.isManager && selected.includes(lead.number) && (
              <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700 }}>✓</Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }} onClick={() => setExpanded((x) => ({ ...x, [lead.number]: !open }))}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.3 }} noWrap>{nameOf(lead) || lead.number}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {nameOf(lead) ? `${lead.number} · ` : ''}
              {segment === 'webinar' ? `registered ${fromNow(lead.webinar_registered_at)}` : `installed ${fromNow(lead.installed_at)}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {(sales.isManager || !lead.owner) && <Tag tone={lead.owner ? 'neutral' : 'warning'}>{lead.owner || 'Unassigned'}</Tag>}
            {segment === 'webinar'
              ? <Tag tone={stage.tone}>{stage.label}</Tag>
              : lead.is_webinar_user && <Tag tone="info">Webinar</Tag>}
            {lead.dealer_name && <Tag tone="warning">Dealer · {lead.dealer_name}</Tag>}
          </Box>
        </Box>
        {signalsOf(lead).length > 0 && <Box sx={{ mt: 1 }}><Signals lead={lead} /></Box>}
        {(lead.algo_plan || lead.research_plan || lead.paid_count > 0 || lead.live_algos > 0 || (segment === 'webinar' && lead.webinar_paid_amount > 0)) && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1.5, rowGap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {(lead.algo_plan || lead.research_plan) && <PlanText lead={lead} />}
            {lead.paid_count > 0 && <Typography variant="caption" color="text.secondary">Paid <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{inr(lead.lifetime_paid)}</Box></Typography>}
            {lead.live_algos > 0 && (
              <Typography variant="caption" color="text.secondary">
                {lead.live_algos} live · {inr(lead.capital_deployed)} · <Box component="span" sx={{ ...pnlSx(lead.gross_pnl), fontWeight: 600 }}>P&L {inr(lead.gross_pnl)}</Box>
              </Typography>
            )}
            {segment === 'webinar' && lead.webinar_paid_amount > 0 && <Typography variant="caption" color="text.secondary">Webinar {inr(lead.webinar_paid_amount)}</Typography>}
          </Box>
        )}
        <Box sx={{ mt: 1.25, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {actions(lead, true)}
          <Button size="small" color="inherit" endIcon={open ? <CollapseIcon /> : <ExpandIcon />} onClick={() => setExpanded((x) => ({ ...x, [lead.number]: !open }))} sx={{ color: 'text.secondary' }}>{open ? 'Less' : 'Details'}</Button>
        </Box>
        <Collapse in={open} unmountOnExit>
          {segment === 'webinar' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Checkbox size="small" checked={Boolean(lead.dialed)} onChange={(e) => saveWebinarNote(lead, { dialed: e.target.checked })} />
              <Typography variant="body2">Dialed</Typography>
              <TextField size="small" fullWidth placeholder="Remarks" defaultValue={lead.remarks || ''} onBlur={(e) => e.target.value !== (lead.remarks || '') && saveWebinarNote(lead, { remarks: e.target.value })} />
            </Box>
          )}
          {segment !== 'webinar' && visibleColumns.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
              {visibleColumns.map((c) => (
                <Box key={c.id} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">{c.name}</Typography>
                  {fieldEditor(lead, c)}
                </Box>
              ))}
            </Box>
          )}
          <LeadDetails lead={lead} onFollowupChanged={load} />
        </Collapse>
      </Paper>
    );
  };

  // ---- desktop: table
  const head = {};
  const stickyRight = { position: 'sticky', right: 0, boxShadow: '-8px 0 8px -8px rgba(16, 24, 40, 0.10)' };
  const table = (
    <TableContainer sx={{ maxHeight: 'calc(100dvh - 270px)', minHeight: 200, '& tr:hover .cell-add': { opacity: 1 } }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {sales.isManager && (
              <TableCell padding="checkbox" sx={head}>
                <Checkbox size="small" checked={allOnPage} indeterminate={!allOnPage && selected.length > 0} onChange={() => setSelected(allOnPage ? [] : rows.map((r) => r.number))} />
              </TableCell>
            )}
            <TableCell sx={head}>Lead</TableCell>
            {sales.isManager && <TableCell sx={head}>Owner</TableCell>}
            <TableCell sx={head}>Why now</TableCell>
            {segment === 'webinar' && <TableCell sx={head}>Webinar</TableCell>}
            <TableCell sx={head}>Plan</TableCell>
            <TableCell sx={head} align="right">Paid</TableCell>
            <TableCell sx={head} align="right">Algos</TableCell>
            {segment === 'webinar' ? (
              <>
                <TableCell sx={head}>Dialed</TableCell>
                <TableCell sx={head}>Remarks</TableCell>
              </>
            ) : visibleColumns.map((c) => (
              <TableCell key={c.id} sx={head}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  {c.name}
                  {sales.isManager && (
                    <IconButton size="small" sx={{ p: 0.25, opacity: 0.45, '&:hover': { opacity: 1 } }} onClick={() => setColumnDialog({ id: c.id, name: c.name, type: c.type, values: (c.dropdown_values || []).join(', ') })}>
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            ))}
            <TableCell sx={{ ...head, ...stickyRight, zIndex: 3 }} align="right">
              {sales.isManager && segment !== 'webinar' && (
                <Tooltip title="Add a column"><IconButton size="small" sx={{ color: 'text.secondary', p: 0.25 }} onClick={() => setColumnDialog({ name: '', type: 'short_text', values: '' })}><AddColumnIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((lead) => {
            const open = Boolean(expanded[lead.number]);
            const stage = webinarStage(lead);
            const colSpan = 20 + visibleColumns.length;
            return (
              <React.Fragment key={lead.number}>
                <TableRow hover selected={selected.includes(lead.number)} sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                  {sales.isManager && (
                    <TableCell padding="checkbox" sx={{ pl: 1 }}><Checkbox size="small" checked={selected.includes(lead.number)} onChange={() => toggleSelect(lead.number)} /></TableCell>
                  )}
                  <TableCell sx={{ minWidth: 230, cursor: 'pointer' }} onClick={() => setExpanded((x) => ({ ...x, [lead.number]: !open }))}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Initials name={nameOf(lead)} seed={lead.number} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', maxWidth: 220 }} noWrap>{nameOf(lead) || lead.number}</Typography>
                          {segment !== 'webinar' && lead.is_webinar_user && <Tag tone="info" sx={{ height: 18, px: 0.75, fontSize: '0.65rem' }}>Webinar</Tag>}
                          {lead.dealer_name && <Tag tone="warning" sx={{ height: 18, px: 0.75, fontSize: '0.65rem' }}>Dealer · {lead.dealer_name}</Tag>}
                          {open ? <CollapseIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
                          {nameOf(lead) ? `${lead.number} · ` : ''}{segment === 'webinar' ? 'Registered' : 'Installed'} {fmtDate(segment === 'webinar' ? lead.webinar_registered_at : lead.installed_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {sales.isManager && <TableCell><OwnerTag owner={lead.owner} /></TableCell>}
                  <TableCell sx={{ minWidth: 180, maxWidth: 260 }}><Signals lead={lead} /></TableCell>
                  {segment === 'webinar' && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tag tone={stage.tone}>{stage.label}</Tag>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                        {/^webinar_/.test(lead.webinar_name || '') ? 'App webinar' : 'Main'}{lead.webinar_paid_amount > 0 ? ` · ${inr(lead.webinar_paid_amount)}` : ''}{lead.webinar_attended ? ' · attended' : ''}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: 150 }}><PlanText lead={lead} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {lead.paid_count > 0 ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{inr(lead.lifetime_paid)}</Typography>
                        <Typography variant="caption" color="text.secondary">{lead.paid_count} payment{lead.paid_count === 1 ? '' : 's'} · {fmtDate(lead.last_paid_at)}</Typography>
                      </>
                    ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {lead.live_algos > 0 ? (
                      <>
                        <Typography variant="body2"><Box component="span" sx={{ fontWeight: 600 }}>{lead.live_algos} live</Box> · {inr(lead.capital_deployed)}</Typography>
                        <Typography variant="caption" sx={{ ...pnlSx(lead.gross_pnl), fontWeight: 600 }}>P&L {lead.gross_pnl > 0 ? '+' : ''}{inr(lead.gross_pnl)}</Typography>
                      </>
                    ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  {segment === 'webinar' ? (
                    <>
                      <TableCell padding="checkbox"><Checkbox size="small" checked={Boolean(lead.dialed)} onChange={(e) => saveWebinarNote(lead, { dialed: e.target.checked })} /></TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <TextField
                          size="small"
                          variant="standard"
                          fullWidth
                          placeholder="Add remark"
                          defaultValue={lead.remarks || ''}
                          key={`${lead.number}-${lead.remarks || ''}`}
                          onBlur={(e) => e.target.value !== (lead.remarks || '') && saveWebinarNote(lead, { remarks: e.target.value })}
                        />
                      </TableCell>
                    </>
                  ) : visibleColumns.map((c) => (
                    <TableCell key={c.id} sx={{ minWidth: c.type === 'long_text' ? 180 : 90, maxWidth: 260 }}>{fieldEditor(lead, c)}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', ...stickyRight, bgcolor: 'inherit', backgroundColor: selected.includes(lead.number) ? '#EEF3FF' : '#fff', 'tr:hover > &': { backgroundColor: selected.includes(lead.number) ? '#EEF3FF' : '#F7F9FF' } }}>{actions(lead)}</TableCell>
                </TableRow>
                {open && (
                  <TableRow>
                    <TableCell colSpan={colSpan} sx={{ bgcolor: '#FAFBFC', px: 2 }}>
                      <LeadDetails lead={lead} onFollowupChanged={load} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  // One-tap view pills.
  const pill = (on) => ({
    flexShrink: 0, height: 28, borderRadius: '14px', fontWeight: 500, fontSize: '0.78rem', border: '1px solid',
    borderColor: on ? 'primary.main' : 'divider', bgcolor: on ? '#EAF0FF' : '#fff', color: on ? '#2F54D6' : '#475467',
    '&:hover': { bgcolor: on ? '#DFE8FF' : '#F5F6F8' }, '& .MuiChip-label': { px: 1.5 }
  });
  const filtered = filterCount > 0 || preset || search || returning;

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Search, filter, sort */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', p: 1.5, pb: 1 }}>
          <TextField
            size="small"
            placeholder="Search number, name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 260 }, maxWidth: { sm: 380 }, '& .MuiOutlinedInput-root': { bgcolor: '#F7F8FA' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: (searchInput || search) ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); reset(); }}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <Button variant={filterCount > 0 ? 'contained' : 'outlined'} startIcon={<FilterListIcon sx={{ fontSize: 18 }} />} onClick={() => setFiltersOpen(true)}>
            Filters{filterCount > 0 ? ` · ${filterCount}` : ''}
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
          <TextField
            select
            size="small"
            value={currentSort?.key || 'priority'}
            onChange={(e) => { setSortBy(e.target.value); setSortOrder(''); reset(); }}
            sx={{ minWidth: { xs: 0, sm: 190 }, flex: { xs: '1 1 120px', sm: '0 0 auto' } }}
            SelectProps={{ renderValue: (v) => <Box component="span"><Box component="span" sx={{ color: 'text.secondary' }}>Sort: </Box>{(sorts.find((x) => x.key === v) || {}).label}</Box> }}
          >
            {sorts.map((x) => <MenuItem key={x.key} value={x.key}>{x.label}</MenuItem>)}
          </TextField>
          {currentSort?.key !== 'priority' && (
            <Tooltip title={effectiveOrder === 'ASC' ? 'Ascending' : 'Descending'}>
              <IconButton size="small" onClick={() => { setSortOrder(effectiveOrder === 'ASC' ? 'DESC' : 'ASC'); reset(); }} sx={{ border: 1, borderColor: 'divider', width: 34, height: 34 }}>
                {effectiveOrder === 'ASC' ? <ArrowUpIcon sx={{ fontSize: 17 }} /> : <ArrowDownIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={meta?.refreshed_at ? `Data refreshed ${fromNow(meta.refreshed_at)}` : 'Refresh'}>
            <IconButton size="small" onClick={load} sx={{ border: 1, borderColor: 'divider', width: 34, height: 34, color: 'text.secondary' }}><RefreshIcon sx={{ fontSize: 17 }} /></IconButton>
          </Tooltip>
        </Box>

        {/* One-tap views */}
        <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', px: 1.5, pb: 1.25, '&::-webkit-scrollbar': { display: 'none' } }}>
          {(meta?.presets || []).map((p) => (
            <Chip key={p.key} label={p.label} clickable onClick={() => applyPreset(p.key)} sx={pill(preset === p.key)} />
          ))}
          <Box sx={{ width: '1px', bgcolor: 'divider', flexShrink: 0, my: 0.5 }} />
          {dates.map((q) => (
            <Chip
              key={q.key}
              label={q.label}
              clickable
              onClick={() => {
                const next = { ...filters };
                if (activeQuick?.key === q.key) { delete next[q.from]; delete next[q.to]; } else Object.assign(next, q.set);
                setFilters(next);
                reset();
              }}
              sx={pill(activeQuick?.key === q.key)}
            />
          ))}
          {segment === 'app' && (
            <Chip label={returningLoading ? 'Loading…' : 'Returning users'} clickable disabled={returningLoading} onClick={toggleReturning} sx={pill(Boolean(returning))} />
          )}
        </Box>

        {/* Result line + bulk actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1.5, py: 0.75, minHeight: 44, borderTop: 1, borderColor: 'divider', bgcolor: selected.length > 0 ? '#EEF3FF' : '#FAFBFC' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {loading && rows.length === 0 ? 'Loading…' : selected.length > 0 ? (
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{selected.length} selected</Box>
            ) : (
              <>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{total.toLocaleString('en-IN')}</Box> lead{total === 1 ? '' : 's'}
                {total > 0 && ` · ${from}–${to}`}
              </>
            )}
          </Typography>
          {filtered && selected.length === 0 && <Button size="small" color="inherit" onClick={clearAll} startIcon={<ClearIcon sx={{ fontSize: 15 }} />} sx={{ color: 'text.secondary' }}>Clear</Button>}
          {sales.isManager && selected.length > 0 && (
            <>
              <Button size="small" color="inherit" onClick={() => setSelected([])} sx={{ color: 'text.secondary' }}>Cancel</Button>
              <Button size="small" variant="contained" startIcon={<ReassignIcon />} onClick={() => setReassign({ numbers: selected })}>
                Reassign {selected.length}
              </Button>
            </>
          )}
          {sales.isManager && selected.length === 0 && total > 0 && filtered && (
            <Button size="small" variant="outlined" startIcon={<ReassignIcon />} onClick={() => setReassign({ all: true })}>
              Reassign all {total.toLocaleString('en-IN')}
            </Button>
          )}
        </Box>

        {!isMobile && rows.length > 0 && (
          <Box sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s', borderTop: 1, borderColor: 'divider' }}>{table}</Box>
        )}
        {!loading && !error && rows.length === 0 && !restricted && (
          <Box sx={{ p: 5, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 600 }}>No leads match</Typography>
            <Typography variant="body2" color="text.secondary">Try a different view or fewer filters.</Typography>
            {filtered && <Button size="small" variant="outlined" onClick={clearAll} sx={{ mt: 1.5 }}>Clear filters</Button>}
          </Box>
        )}
        {loading && rows.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', p: 5, borderTop: 1, borderColor: 'divider' }}><CircularProgress size={28} /></Box>}
      </Paper>

      {error && <Alert severity="error" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}
      {restricted && <Alert severity="info" sx={{ mt: 1 }}>{restricted}</Alert>}

      {isMobile && rows.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1, opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }}>{rows.map(card)}</Stack>
      )}

      {total > limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Pagination count={Math.ceil(total / limit)} page={page} onChange={(_, p) => { setPage(p); setSelected([]); window.scrollTo({ top: 0 }); }} size={isMobile ? 'small' : 'medium'} siblingCount={isMobile ? 0 : 1} />
          <TextField select size="small" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); reset(); }} sx={{ width: 110 }}>
            {PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n} / page</MenuItem>)}
          </TextField>
        </Box>
      )}

      <LeadFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={applyFilters}
        meta={meta}
        segment={segment}
        customColumns={customColumns}
        isManager={sales.isManager}
      />

      {/* Row menu (leaders / admins) */}
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setReassign({ numbers: [menu.lead.number] }); setMenu(null); }}>
          <ReassignIcon fontSize="small" sx={{ mr: 1 }} /> Reassign
        </MenuItem>
      </Menu>

      {/* Follow-up */}
      <Dialog open={Boolean(followupFor)} onClose={() => setFollowupFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Follow-up · {followupFor && (nameOf(followupFor) || followupFor.number)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Note" multiline minRows={2} value={followupForm.note} onChange={(e) => setFollowupForm({ ...followupForm, note: e.target.value })} autoFocus />
            <TextField label="Remind me at" type="datetime-local" value={followupForm.remind_at} onChange={(e) => setFollowupForm({ ...followupForm, remind_at: e.target.value })} InputLabelProps={{ shrink: true }} />
            <Typography variant="caption" color="text.secondary">Only you will see this follow-up.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowupFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveFollowup} disabled={busy || !followupForm.note.trim() || !followupForm.remind_at}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Reassign */}
      <Dialog open={Boolean(reassign)} onClose={() => setReassign(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reassign {reassign?.all ? `all ${total.toLocaleString('en-IN')} matching leads` : `${reassign?.numbers?.length || 0} lead${reassign?.numbers?.length === 1 ? '' : 's'}`}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Assign to" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} sx={{ mt: 1 }}>
            {sales.isAdmin && <MenuItem value="">Nobody (unassign)</MenuItem>}
            {members.map((m) => <MenuItem key={m.owner} value={m.owner}>{m.owner}{m.leader ? ' (leader)' : ''}</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            The lead moves everywhere at once (app lead, webinar lead and CRM). Payments already made keep their revenue credit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassign(null)}>Cancel</Button>
          <Button variant="contained" onClick={doReassign} disabled={busy || (!reassignTo && !sales.isAdmin)}>Reassign</Button>
        </DialogActions>
      </Dialog>

      {/* Add / edit a custom column */}
      <Dialog open={Boolean(columnDialog)} onClose={() => setColumnDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{columnDialog?.id ? 'Edit column' : 'Add column'}</DialogTitle>
        <DialogContent>
          {columnDialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" value={columnDialog.name} disabled={Boolean(columnDialog.id)} onChange={(e) => setColumnDialog({ ...columnDialog, name: e.target.value })} />
              <TextField select label="Type" value={columnDialog.type} disabled={Boolean(columnDialog.id)} onChange={(e) => setColumnDialog({ ...columnDialog, type: e.target.value })}>
                <MenuItem value="short_text">Short text</MenuItem>
                <MenuItem value="long_text">Long text</MenuItem>
                <MenuItem value="dropdown">Dropdown</MenuItem>
              </TextField>
              {columnDialog.type === 'dropdown' && (
                <TextField label="Options (comma separated)" multiline value={columnDialog.values} onChange={(e) => setColumnDialog({ ...columnDialog, values: e.target.value })} />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {columnDialog?.id && <Button color="error" startIcon={<DeleteIcon />} onClick={deleteColumn} sx={{ mr: 'auto' }}>Delete</Button>}
          <Button onClick={() => setColumnDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveColumn} disabled={!columnDialog?.name?.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadsBoard;
